import React from "react";
import { useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import { Building2, Briefcase, Download, BookOpen, Menu, X, Loader2, Coins, LogOut } from "lucide-react";
import type { OrgStats, WorkspaceItem } from "../types";

interface OrgHeaderProps {
  stats: OrgStats;
  workspaces: WorkspaceItem[];
  selectedWorkspace: WorkspaceItem | null;
  onSelectWorkspace: (ws: WorkspaceItem) => void;
  onExportCSV: () => void;
  isExporting?: boolean;
  onToggleMobileNav?: () => void;
  isMobileNavOpen?: boolean;
  creditsSummary?: { ai_credits_balance?: number } | null;
  onOpenBilling?: () => void;
}

export const OrgHeader: React.FC<OrgHeaderProps> = ({
  stats,
  workspaces,
  selectedWorkspace,
  onSelectWorkspace,
  onExportCSV,
  isExporting = false,
  onToggleMobileNav,
  isMobileNavOpen = false,
  creditsSummary,
  onOpenBilling,
}) => {
  const navigate = useNavigate();
  const { signOut } = useClerk();

  const handleSignOut = () => {
    void signOut().then(() => {
      navigate("/sign-in");
    });
  };

  return (
    <header className="border-b border-slate-200 bg-white/95 backdrop-blur-xl sticky top-0 z-30 shadow-2xs">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-3.5 flex flex-col md:flex-row md:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 shadow-2xs flex-shrink-0">
              {stats.logo_url ? (
                <img
                  src={stats.logo_url}
                  alt={stats.organization_name}
                  className="w-8 h-8 sm:w-9 sm:h-9 object-contain rounded-xl"
                />
              ) : (
                <Building2 className="w-5 h-5 sm:w-6 sm:h-6" />
              )}
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight truncate max-w-xs sm:max-w-md" title={stats.organization_name}>
                  {stats.organization_name}
                </h1>
                <span className="px-2 py-0.5 bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px] font-bold uppercase rounded-md">
                  B2B Hub
                </span>
                {stats.industry && (
                  <span className="px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700 text-[10px] font-medium rounded-md">
                    {stats.industry}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2 sm:gap-3 mt-1 flex-wrap">
                <p className="text-xs text-slate-500 font-medium line-clamp-1">
                  {stats.description || "Corporate AI Learning & Progress Dashboard"}
                </p>

                {workspaces.length > 0 && (
                  <div className="flex items-center gap-1.5 bg-indigo-50/80 border border-indigo-200/80 rounded-lg px-2 py-0.5 text-xs shadow-2xs">
                    <Briefcase className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
                    <span className="text-indigo-900 font-semibold text-[11px]">Workspace:</span>
                    <select
                      value={selectedWorkspace?.slug || ""}
                      onChange={(e) => {
                        const found = workspaces.find((w) => w.slug === e.target.value);
                        if (found) onSelectWorkspace(found);
                      }}
                      className="bg-transparent text-indigo-700 font-bold focus:outline-none cursor-pointer pr-1 text-xs"
                      aria-label="Select organization workspace"
                    >
                      {workspaces.map((w) => (
                        <option key={w.id} value={w.slug}>
                          {w.name} {w.is_default ? "★" : ""} ({w.member_count})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile hamburger menu toggle */}
          {onToggleMobileNav && (
            <button
              onClick={onToggleMobileNav}
              className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl md:hidden cursor-pointer"
              aria-label="Toggle navigation menu"
            >
              {isMobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 sm:gap-3 self-end md:self-center">
          {creditsSummary && onOpenBilling && (
            <button
              onClick={onOpenBilling}
              className="px-3 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 border border-indigo-200 text-indigo-900 rounded-xl text-xs font-bold flex items-center gap-1.5 transition shadow-2xs cursor-pointer group"
              title="Click to manage AI course credits"
            >
              <Coins className="w-3.5 h-3.5 text-indigo-600 fill-indigo-200 group-hover:scale-110 transition duration-150" />
              <span>{creditsSummary.ai_credits_balance ?? 0}</span>
              <span className="text-[10px] font-semibold text-indigo-600 uppercase">Credits</span>
            </button>
          )}

          <button
            onClick={onExportCSV}
            disabled={isExporting}
            className="px-3.5 py-2 bg-white hover:bg-slate-50 disabled:opacity-60 border border-slate-200 hover:border-slate-300 text-slate-700 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition shadow-2xs cursor-pointer"
          >
            {isExporting ? (
              <Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5 text-slate-500" />
            )}
            <span className="hidden sm:inline">{isExporting ? "Exporting..." : "Export Report"}</span>
            <span className="sm:hidden">{isExporting ? "..." : "Export"}</span>
          </button>
          <button
            onClick={() => navigate("/catalog")}
            className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-2xs flex items-center gap-1.5 transition cursor-pointer"
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span>Learning Catalog</span>
          </button>
          <button
            onClick={handleSignOut}
            className="px-3 py-2 bg-white hover:bg-rose-50 border border-slate-200 hover:border-rose-200 text-slate-700 hover:text-rose-600 text-xs font-semibold rounded-xl flex items-center gap-1.5 transition shadow-2xs cursor-pointer"
            title="Sign Out"
          >
            <LogOut className="w-3.5 h-3.5 text-slate-500 hover:text-rose-600" />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </div>
    </header>
  );
};
